import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import MachinesConcerneeListViewData from './machinesConcerneeListViewData';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { AddMachinesConcernee } from './addMachinesConcernee';
import { DeleteRecord } from '../../components/deleteRecord';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FixedSizeList } from "react-window";
import NullDataBox from '../nullDataBox';
import { debounce } from 'lodash';
import Iconify from '../Iconify';
import '../../css/MachinesConcernee.css';

const MachinesConcernee = React.memo(({ chooseSingleMachine = false, machines, selectedMachines, setSelectedMachines, openAddMachines, setOpenAddMachines,
    openRemoveMachine, setOpenRemoveMachine, hasPermission, allMachinesSelected, setAllMachinesSelected,
    addMachinesFilter, setAddMachinesFilter, handleUpdateMachines, handleRemoveMachine }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const prevScrollTop = useRef(0);
    const machinesCount = machines?.length

    const [machinesFetched, setMachinesFetched] = useState();
    const [machineToRemove, setMachineToRemove] = useState('');
    const [machineColumnWidth, setMachineColumnWidth] = useState(
        JSON.parse(localStorage.getItem('machineColumnWidth')) ||
        {
            name: 250,
            site: 250,
            pole: 250,
            metier: 250,
            marque: 250,
            serial_number: 250,
        }
    );

    const tableHeadRef = useRef(null);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useEffect(() => {
        setMachinesFetched((Math.min(machinesCount, 7)));
    }, [machinesCount]);

    // Handle scroll and display more items
    const handleScroll = debounce(({ scrollOffset }) => {
        // Check if the scroll is vertical (scrollOffset has changed)
        if (scrollOffset !== prevScrollTop.current) {
            // Calculate the new offset based on the scroll position and the item height
            const newOffset = Math.floor((scrollOffset / 70) + 1)

            prevScrollTop.current = scrollOffset;

            // Set the number of fetched items depending on the screen height
            const newFetchedValue = newOffset + Math.min(machinesCount, 7);
            const finalFetchedValue = Math.min(newFetchedValue, machinesCount);

            setMachinesFetched(finalFetchedValue);
        }
    }, 300);

    const handleMachineClick = useCallback((id) => {
        navigate(`/dashboard/machines/machine_details/${id}`)
    }, [navigate]);

    const handleColumnWidthChange = (value, columnKey) => {
        setMachineColumnWidth({ ...machineColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('machineColumnWidth', JSON.stringify(machineColumnWidth));
    }, [machineColumnWidth]);

    const Row = ({ index, style, data }) => {
        const { machines, handleMachineClick } = data;
        const machine = index === 0 ? machines[0] : machines[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <MachinesConcerneeListViewData
                    chooseSingleMachine={chooseSingleMachine}
                    machine={chooseSingleMachine ? machine : machine.machine}
                    columnWidth={machineColumnWidth}
                    handleMachineClick={handleMachineClick}
                    hasPermission={hasPermission}
                    setMachineToRemove={setMachineToRemove}
                    setOpenRemoveMachine={setOpenRemoveMachine}
                />
            </Box>
        );
    };

    return (
        <Box className='machinesConcernee_main_box'>
            <Box className='machinesConcernee_title_icon_box'>
                <Typography className='contractData_title'>{chooseSingleMachine ? 'Machine concernée' : 'Machines concernées'}</Typography>
                {(hasPermission && machinesCount > 0) &&
                    <Iconify className='contractData_edit_icon' icon={chooseSingleMachine ? "ic:outline-edit" : "ic:baseline-plus"} onClick={() => setOpenAddMachines(true)} />
                }
            </Box>
            {(hasPermission && machinesCount) === 0 ?
                <NullDataBox
                    description={
                        chooseSingleMachine ?
                            'Ajoutez une machine associée. Sélectionnez la machine pertinente pour compléter cette action.' :
                            'Ajoutez des machines associées. Sélectionnez les machines pertinentes pour compléter cette action.'
                    }
                    buttonTitle={chooseSingleMachine ? 'Ajouter une Machine' : 'Ajouter des Machines'}
                    setOpenDialog={setOpenAddMachines}
                />
                :
                <Box className='machinesConcernee_main_box'>
                    {!chooseSingleMachine &&
                        <Typography className='machinesConcernee_total_number'>
                            {machinesFetched} {` / ${machinesCount} résultat`}
                        </Typography>
                    }
                    <Box className='table'>
                        <Box className='tableHead' ref={tableHeadRef}>
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={t('machineName')}
                                columnKey='name'
                                isFixed={true}
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                                backgroundColor='white'
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={t('sites')}
                                columnKey='site'
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={t('poles')}
                                columnKey='pole'
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={t('Métier')}
                                columnKey='metier'
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={t('Marque')}
                                columnKey='marque'
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground'
                                label={`Numéro de série`}
                                columnKey='serial_number'
                                columnWidth={machineColumnWidth}
                                onColumnWidthChange={handleColumnWidthChange}
                            />
                            <CustomHeaderCell
                                className='table_header_cell_non_filter whiteBackground' />
                        </Box>
                        <Box className='tableBody'>
                            <FixedSizeList
                                ref={scrollRef}
                                height={(Math.min(machinesCount, 7) * 70) + 15}
                                width='100%'
                                itemCount={machinesCount}
                                itemSize={70}
                                itemData={{ machines, handleMachineClick, handleRemoveMachine }}
                                onScroll={handleScroll}
                            >
                                {Row}
                            </FixedSizeList>
                        </Box>
                    </Box>
                </Box>
            }

            {openAddMachines &&
                <AddMachinesConcernee
                    chooseSingleMachine={chooseSingleMachine}
                    openAddMachines={openAddMachines}
                    setOpenAddMachines={setOpenAddMachines}
                    addMachines={handleUpdateMachines}
                    selectedMachines={selectedMachines}
                    setSelectedMachines={setSelectedMachines}
                    addMachinesFilter={addMachinesFilter}
                    setAddMachinesFilter={setAddMachinesFilter}
                    allMachinesSelected={allMachinesSelected}
                    setAllMachinesSelected={setAllMachinesSelected}
                    machine_ids={chooseSingleMachine ? machines : machines?.map((machine) => machine.machine.id)}
                />
            }

            {openRemoveMachine &&
                <DeleteRecord
                    openDelete={openRemoveMachine}
                    setOpenDelete={setOpenRemoveMachine}
                    handleDelete={() => handleRemoveMachine(machineToRemove)}
                    title="Êtes-vous sûr du vouloir supprimer la machine ?"
                />
            }

        </Box>

    );
});

export default MachinesConcernee;

